import { useMemo } from 'react';

import { addNarrativeRequest } from 'utilitys/narrative/narrativeTools';

import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';

import { useQueryRiskDensityBy } from 'api/queries/riskDensityBy.query';

import { getInputLabel } from 'components/common/CustomFilters/TableDateRangeFilter/TableDateRangeFilter.utils';
import { FilterNames, getFiltersData } from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';

import { usePinboardWidgetWrapperContext } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/context/widgetWrapperContext';
import { resolutionOptions } from 'components/widgets/RiskDensity/RiskDensity.utils';

export const useRiskDensityProvider = ({
    widgetId,
    contractId,
    projectId,
    latestVersionId,
    globalFilterData,
    localFilters,
    dateRange,
    resolution,
    selectBy,
    startProjectDate,
    endProjectDate,
    dateRangeOption,
    riskDensityBy,
    componentKey,
}) => {
    const { isGenerativeAi } = usePinboardWidgetWrapperContext();

    const source = widgetId
        ? localFilters
        : {
              ...globalFilterData,
              dateRange,
              resolution,
              startProjectDate,
              endProjectDate,
              dateRangeOption,
              riskDensityBy,
              selectBy,
          };

    const filters = useMemo(
        () =>
            getFiltersData({
                [FilterNames.ACTIVITY_CODES]: source?.activityCodes
                    ?.filter((item) => !item.value.isExclude)
                    .map((i) => i.value.name),
                [FilterNames.ACTIVITY_CODES_EXCLUDE]: source?.activityCodes
                    ?.filter((item) => item.value.isExclude)
                    .map((i) => i.value.name),
                [FilterNames.FRAGNET_SEARCH]: source?.milestoneFragnet,
                [FilterNames.TAG_CARD_BOARD]: source?.tags
                    ?.filter((item) => !item.value.isExclude)
                    .map((i) => i.value.name),
                [FilterNames.TAG_CARD_BOARD_EXCLUDE]: source?.tags
                    ?.filter((item) => item.value.isExclude)
                    .map((i) => i.value.name),
                [FilterNames.WBS_SEARCH]: source?.wbs,
            }),

        [source.activityCodes, source.milestoneFragnet, source.tags, source.wbs, globalFilterData],
    );

    const pillsData = {
        wbs: source?.wbs,
        milestoneFragnet: source?.milestoneFragnet,
        activityCodes: source?.activityCodes?.filter((item) => !item.value?.isExclude).map((i) => i.value?.name),
        activityCodesExclude: source?.activityCodes?.filter((item) => item.value?.isExclude).map((i) => i.value?.name),
        tags: source?.tags?.filter((item) => !item.value?.isExclude).map((i) => i.value?.name),
        tagsExclude: source?.tags?.filter((item) => item.value?.isExclude).map((i) => i.value?.name),
        resolution: resolutionOptions.find((item) => item.value === source?.resolution)?.label,
        dateRange: getInputLabel(source?.dateRange),
    };

    const { pillsList: pills } = useGlobalFiltersHook({ widgetId, source: pillsData, latestVersionId });

    const payload = useMemo(() => {
        return {
            id: latestVersionId ? [latestVersionId] : [],
            filter: {
                filters,
            },
            startDate: source?.dateRange?.startDate,
            endDate: source?.dateRange?.endDate,
            isWeekResolution: source?.resolution,
            selectBy: source?.selectBy,
            ...addNarrativeRequest(isGenerativeAi, componentKey, widgetId),
        };
    }, [latestVersionId, filters, source?.dateRange, source?.resolution, source?.selectBy, isGenerativeAi]);

    const state = {
        project: {
            projectId,
            contractId,
            latestVersionId,
        },
        globalFilterData: {
            activityCodes: source?.activityCodes,
            milestoneFragnet: source?.milestoneFragnet,
            tags: source?.tags,
            wbs: source?.wbs,
        },
        local: {
            dateRange: source?.dateRange,
            resolution: source?.resolution,
            startProjectDate: source?.startProjectDate,
            endProjectDate: source?.endProjectDate,
            dateRangeOption: source?.dateRangeOption,
            riskDensityBy: source?.riskDensityBy,
            selectBy: source?.selectBy,
        },
    };

    return {
        ui: {
            state: {
                ...state.project,
                ...state.globalFilterData,
                ...state.local,
            },
            isInPinboard: Boolean(widgetId),
            pills,
        },
        api: {
            riskDensityBy: useQueryRiskDensityBy(payload),
        },
    };
};
